/**
 * Доля хвоста публичного рендерера: вид `video_embed` (встроенный ролик YouTube/Vimeo,
 * а с 21.10.2026 — и файл видео mp4/webm/mov/m4v тегом `<video>`, см. `VideoFileBlock`).
 *
 * WEB-2920, заход 2: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово вместе с разбором ссылки и плеером `VideoEmbedBlock`. Метка `case 'video_embed'` и хуки остаются в фасаде; ветка вида —
 * функция отрисовки `renderVideoEmbed(ctx)`, замыкания фасада приходят
 * объектом-контекстом (`ExtendedRenderContext`). Эту долю импортирует только
 * фасад: она едет в ленивом куске `extended`, а не в первом экране
 * (самопроверка обхода).
 */
import React, { useRef, useState } from 'react';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { useEditorPreview } from '../EditorPreviewContext';
import { usePageShown } from '@/public/useAppShownSignal';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';
import { isVideoFileUrl, readVideoFile } from '../videoFile';

// WEB-2451 п.2: разбор ссылки — общий с панелью свойств модуль (Shorts и
// адрес встраивания распознаются вдобавок к прежним видам).
import { parseVideoEmbed, type ParsedVideo } from '../videoEmbedUrl';

/**
 * WEB-956 — рецепт «чистого» плеера.
 *
 * Что реально убирается параметрами:
 *  YouTube (домен youtube-nocookie.com — не пишет куки до старта воспроизведения):
 *    controls=0        — панель управления и ПРОГРЕССБАР (перемотка);
 *    modestbranding=1  — логотип YouTube в панели;
 *    rel=0             — похожие ролики в конце берутся только с того же канала
 *                        (полностью отключить «дальше» YouTube больше не даёт);
 *    playsinline=1     — на iOS играет в потоке, а не открывает нативный плеер;
 *    iv_load_policy=3  — аннотации.
 *
 *  ЧЕГО УБРАТЬ НЕЛЬЗЯ (проверено, это предел для iframe-эмбеда):
 *    параметр showinfo YouTube выпилил в 2018-м, и оверлей с НАЗВАНИЕМ ролика
 *    + аватаром канала + кнопкой «Смотреть на YouTube» показывается поверх
 *    видео на ПАУЗЕ, при наведении и в конце ролика. Никакой комбинацией
 *    параметров он не снимается (снять его может только YouTube Player API у
 *    партнёров, либо это нарушение ToS).
 *    Поэтому «чистота» вытягивается вторым приёмом: пока пользователь не
 *    нажал play, iframe вообще НЕ создаётся — на экране наш собственный
 *    постер. Оверлей YouTube физически не может появиться до старта, а после
 *    старта (пока играет) он и так скрыт.
 *
 *  Vimeo: title=0 (название), byline=0 (автор), portrait=0 (аватар),
 *    controls=0 (панель + прогрессбар), dnt=1 (не трекать зрителя).
 */
function buildVideoEmbedSrc(
  video: ParsedVideo,
  opts: {
    muted: boolean;
    showControls: boolean;
    autoplay: boolean;
    /** WEB-969: мост для команды unMute кликом (режим «без звука при автопоказе»). */
    enableJsApi?: boolean;
  },
): string {
  const controls = opts.showControls ? '1' : '0';
  const autoplay = opts.autoplay ? '1' : '0';
  const muted = opts.muted ? '1' : '0';
  if (video.provider === 'youtube') {
    const params = new URLSearchParams({
      controls,
      modestbranding: '1',
      rel: '0',
      playsinline: '1',
      iv_load_policy: '3',
      autoplay,
      mute: muted,
      ...(opts.enableJsApi ? { enablejsapi: '1' } : {}),
    });
    return `https://www.youtube-nocookie.com/embed/${video.id}?${params.toString()}`;
  }
  const params = new URLSearchParams({
    title: '0',
    byline: '0',
    portrait: '0',
    controls,
    autoplay,
    muted,
    dnt: '1',
  });
  return `https://player.vimeo.com/video/${video.id}?${params.toString()}`;
}

/** Кнопка ▶ на постере — общая у ролика по ссылке и ролика-файла. */
const PLAY_BADGE_STYLE: React.CSSProperties = {
  position: 'relative',
  width: 64,
  height: 64,
  borderRadius: '50%',
  background: 'rgba(0, 0, 0, 0.55)',
  border: '2px solid rgba(255, 255, 255, 0.9)',
  color: '#fff',
  fontSize: 24,
  lineHeight: '60px',
  textAlign: 'center',
  paddingLeft: 4,
  boxSizing: 'border-box',
};

/**
 * WEB-956 — блок video_embed.
 *
 * ДЕФОЛТЫ ДЛЯ СТАРЫХ БЛОКОВ: полей videoAutoplay/videoMuted/videoShowControls
 * у уже сохранённых воронок нет, и они получают новое поведение (постер +
 * запуск по клику, без звука, без прогрессбара). Это ОСОЗНАННАЯ смена вида
 * существующих воронок по требованию PM, а не недосмотр совместимости —
 * закреплено тестом в PublicBlockRendererExtended.videoEmbed.web956.test.tsx.
 *
 * Автозапуск «при показе экрана» браузеры разрешают ТОЛЬКО без звука, поэтому
 * при videoAutoplay='screen' звук глушится принудительно, что бы ни стояло в
 * videoMuted — иначе автозапуск просто не сработает.
 *
 * Шрам WEB-846: в редакторском превью (isEditorPreview) боевого проигрывания
 * нет — ни по клику, ни автозапуском: постер остаётся постером.
 */
function VideoEmbedBlock({
  video,
  autoplayMode,
  soundMode,
  unmuteOnClick,
  showControls,
  locale,
}: {
  video: ParsedVideo;
  /** WEB-1428: язык воронки для системных aria-подписей. */
  locale?: string | null;
  autoplayMode: 'click' | 'screen';
  /** WEB-969: 'on' — по клику со звуком; 'off' — тихо всегда;
   * 'muted_autoplay' — авто тихо, клик может включить (unmuteOnClick). */
  soundMode: 'on' | 'off' | 'muted_autoplay';
  unmuteOnClick: boolean;
  showControls: boolean;
}): React.ReactElement {
  const { isEditorPreview } = useEditorPreview();
  const [started, setStarted] = useState(false);
  const [unmuted, setUnmuted] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // WEB-3673: автозапуск на прогретой SDK странице (`?preload=1`) — только
  // после показа человеку; до него стоит постер.
  const shown = usePageShown();
  const autoOnScreen = autoplayMode === 'screen' && !isEditorPreview && shown;
  const showIframe = autoOnScreen || started;
  // WEB-969: автопоказ браузеры пускают ТОЛЬКО тихим — какой бы режим звука
  // ни стоял; звук по клику разрешён (жест пользователя).
  const effectiveMuted = autoplayMode === 'screen' ? true : soundMode !== 'on';
  // Мост клика-в-звук нужен только тихому авто-видео.
  const wantsUnmuteBridge =
    autoOnScreen && soundMode === 'muted_autoplay' && unmuteOnClick;

  const handleUnmute = () => {
    if (isEditorPreview) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    if (video.provider === 'youtube') {
      // Протокол YouTube IFrame API (enablejsapi=1 в src обязателен).
      win.postMessage(
        JSON.stringify({ event: 'command', func: 'unMute', args: '' }),
        '*',
      );
    } else {
      // Vimeo player.js message protocol.
      win.postMessage(JSON.stringify({ method: 'setVolume', value: 1 }), '*');
    }
    setUnmuted(true);
  };
  const posterUrl =
    video.provider === 'youtube'
      ? `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`
      : null; // у Vimeo постер достаётся только через их API — рисуем тёмную заглушку

  return (
    <div
      style={{
        position: 'relative',
        paddingBottom: '56.25%',
        height: 0,
        overflow: 'hidden',
        borderRadius: 'var(--border-radius-md, 8px)',
        marginBottom: 16,
        background: '#000',
      }}
    >
      {showIframe ? (
        <>
          <iframe
            ref={iframeRef}
            data-testid="video-embed-iframe"
            src={buildVideoEmbedSrc(video, {
              muted: effectiveMuted,
              showControls,
              autoplay: true,
              enableJsApi: wantsUnmuteBridge,
            })}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 0,
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Video"
          />
          {wantsUnmuteBridge && !unmuted && (
            // Прозрачный слой «включить звук»: первый тап по тихому авто-видео
            // шлёт плееру unmute и исчезает — дальше клики идут в сам плеер.
            <div
              data-testid="video-embed-unmute-overlay"
              role="button"
              tabIndex={0}
              aria-label={getPublicQuizStrings(locale).unmuteVideoAria}
              onClick={handleUnmute}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleUnmute();
                }
              }}
              style={{
                position: 'absolute',
                inset: 0,
                cursor: 'pointer',
                background: 'transparent',
              }}
            />
          )}
        </>
      ) : (
        <div
          data-testid="video-embed-poster"
          role="button"
          tabIndex={0}
          aria-label={getPublicQuizStrings(locale).playVideoAria}
          onClick={() => {
            if (isEditorPreview) return;
            setStarted(true);
          }}
          onKeyDown={(e) => {
            if (isEditorPreview) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setStarted(true);
            }
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#000',
            cursor: isEditorPreview ? 'default' : 'pointer',
          }}
        >
          {posterUrl && (
            <img
              data-testid="video-embed-poster-image"
              src={posterUrl}
              alt=""
              // WEB-1994: настоящие внутренние размеры кадра YouTube
              // (`hqdefault.jpg` — всегда 480×360). Видимый размер задаёт
              // стиль ниже (100% коробки 16:9), атрибуты нужны браузеру,
              // чтобы знать пропорцию до загрузки.
              width={480}
              height={360}
              decoding="async"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          )}
          <span data-testid="video-embed-play" style={PLAY_BADGE_STYLE}>
            ▶
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Слово владельца 21.10.2026 — ролик-файл (`.mp4/.webm/.mov/.m4v`), как у
 * Moonly: тег `<video playsInline>` во всю ширину блока.
 *
 * Правила звука и запуска — те же, что у YouTube-ветки выше: самозапуск
 * «при показе» всегда без звука; без самозапуска — свой постер с кнопкой,
 * ролик по клику; в превью редактора ролик не играет (шрам WEB-846).
 * Вид — поля `videoLoop` / `videoFit` / `videoBoxHeight` через зажим
 * `readVideoFile`: нет поля — по кругу при самозапуске, целиком, высота по
 * пропорции ролика. Ошибка загрузки — прежняя заглушка с адресом.
 *
 * `muted` React ставит только свойством, не атрибутом, а iOS Safari пускает
 * самозапуск только по атрибуту — поэтому в ref ставится `defaultMuted`: по
 * стандарту HTML он отражается в атрибут `muted` (и снимает его).
 */
function VideoFileBlock({
  url,
  block,
  locale,
  autoplayMode,
  soundMode,
  unmuteOnClick,
  showControls,
}: {
  url: string;
  block: ExtendedRenderContext['block'];
  locale?: string | null;
  autoplayMode: 'click' | 'screen';
  soundMode: 'on' | 'off' | 'muted_autoplay';
  unmuteOnClick: boolean;
  showControls: boolean;
}): React.ReactElement {
  const { isEditorPreview } = useEditorPreview();
  const [started, setStarted] = useState(false);
  const [unmuted, setUnmuted] = useState(false);
  const [failed, setFailed] = useState(false);
  // WEB-3673 × сведение стильпаков: на прогретой SDK странице (`?preload=1`)
  // самозапуск ролика-файла ждёт показа человеку, как видео по ссылке.
  const shown = usePageShown();
  const autoOnScreen = autoplayMode === 'screen' && !isEditorPreview && shown;
  const showVideo = autoOnScreen || started;
  const look = readVideoFile(block, autoplayMode === 'screen');
  const wantsUnmuteBridge =
    autoOnScreen && soundMode === 'muted_autoplay' && unmuteOnClick;
  const muted =
    (autoplayMode === 'screen' ? true : soundMode !== 'on') && !unmuted;

  if (failed) return renderVideoFallback(block);

  const start = () => {
    if (isEditorPreview) return;
    setStarted(true);
  };

  return (
    <div
      data-testid="video-file-box"
      style={{
        position: 'relative',
        width: '100%',
        ...(look.boxHeight !== undefined
          ? { height: look.boxHeight }
          : showVideo
            ? {}
            : { paddingBottom: '56.25%', height: 0 }),
        overflow: 'hidden',
        borderRadius: 'var(--border-radius-md, 8px)',
        marginBottom: 16,
      }}
    >
      {showVideo ? (
        <>
          <video
            data-testid="video-file-player"
            ref={(el) => {
              if (!el) return;
              el.muted = muted;
              el.defaultMuted = muted;
            }}
            src={url}
            autoPlay
            playsInline
            muted={muted}
            loop={look.loop}
            controls={showControls}
            preload="auto"
            onError={() => setFailed(true)}
            style={{
              display: 'block',
              width: '100%',
              height: look.boxHeight !== undefined ? '100%' : 'auto',
              objectFit: look.fit,
            }}
          />
          {wantsUnmuteBridge && !unmuted && (
            <div
              data-testid="video-embed-unmute-overlay"
              role="button"
              tabIndex={0}
              aria-label={getPublicQuizStrings(locale).unmuteVideoAria}
              onClick={() => setUnmuted(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setUnmuted(true);
                }
              }}
              style={{
                position: 'absolute',
                inset: 0,
                cursor: 'pointer',
                background: 'transparent',
              }}
            />
          )}
        </>
      ) : (
        <div
          data-testid="video-embed-poster"
          role="button"
          tabIndex={0}
          aria-label={getPublicQuizStrings(locale).playVideoAria}
          onClick={start}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              start();
            }
          }}
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#000',
            cursor: isEditorPreview ? 'default' : 'pointer',
          }}
        >
          <span data-testid="video-embed-play" style={PLAY_BADGE_STYLE}>
            ▶
          </span>
        </div>
      )}
    </div>
  );
}

/** Ветка `video_embed` фасада — тело `case` слово в слово. */
export function renderVideoEmbed(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const { block, varCtx } = ctx;
  const video = parseVideoEmbed(block.videoUrl ?? '');
  if (video) {
    // WEB-956: дефолты — «чистый» вид, запуск по клику, без звука, без
    // прогрессбара. Старые блоки полей не имеют и получают именно их.
    return (
      <VideoEmbedBlock
        video={video}
        locale={varCtx?.system?.locale}
        autoplayMode={block.videoAutoplay ?? 'click'}
        // WEB-969: новые поля сильнее legacy videoMuted; совсем без полей —
        // прежний дефолт «тихо всегда» (вид старых блоков не меняется).
        soundMode={
          block.videoSound ?? (block.videoMuted === false ? 'on' : 'off')
        }
        unmuteOnClick={block.videoUnmuteOnClick ?? false}
        showControls={block.videoShowControls ?? false}
      />
    );
  }
  // Слово владельца 21.10.2026: адрес не ролик YouTube/Vimeo, а файл видео.
  if (isVideoFileUrl(block.videoUrl ?? '')) {
    return (
      <VideoFileBlock
        url={block.videoUrl ?? ''}
        block={block}
        locale={varCtx?.system?.locale}
        autoplayMode={block.videoAutoplay ?? 'click'}
        soundMode={
          block.videoSound ?? (block.videoMuted === false ? 'on' : 'off')
        }
        unmuteOnClick={block.videoUnmuteOnClick ?? false}
        showControls={block.videoShowControls ?? false}
      />
    );
  }
  return renderVideoFallback(block);
}

/**
 * Заглушка: адрес не разобран (или ролик-файл не загрузился). Берёт сам блок и
 * ветвится записью `block.videoUrl &&` намеренно (WEB-3718): по ней разбор
 * сторожа живых типов (`blockRenderProbe.tsx`) находит состояние «адрес
 * задан» — с локальной переменной он его не видит.
 */
function renderVideoFallback(
  block: Pick<ExtendedRenderContext['block'], 'videoUrl'>,
): React.ReactElement {
  return (
    <div
      style={{
        padding: '24px',
        background: '#000',
        borderRadius: 'var(--border-radius-md, 8px)',
        textAlign: 'center',
        marginBottom: 16,
      }}
    >
      <span style={{ color: '#fff', fontSize: 32 }}>▶</span>
      {block.videoUrl && (
        <p
          style={{
            color: 'var(--color-text-secondary, #8c8c8c)',
            fontSize: 12,
            marginTop: 8,
          }}
        >
          {block.videoUrl}
        </p>
      )}
    </div>
  );
}
